import { useState, useRef, useEffect } from 'react'
import { 
  Button,
  Alert, AlertDescription,
  Badge,
  Progress
} from '@creanizer/ui'
import { 
  Download, Upload, FileText, FileJson, 
  Database, Info, AlertCircle, CheckCircle 
} from 'lucide-react'
import { DataManager } from '@creanizer/utils'
import { useNameStore } from '../../../stores/nameStore'
import { usePlotStore } from '../../../stores/plotStore'
import { useTitleStore } from '../../../stores/titleStore'
import { useCharacterStore } from '../../../stores/characterStore'
import { useRelationshipStore } from '../../../stores/relationshipStore'
import { useWikipediaStore } from '../../../stores/wikipediaStore'
import { useImagePromptStore } from '../../../stores/imagePromptStore'
import { useEnemyStore } from '../../../stores/enemyStore'
import { useItemStore } from '../../../stores/itemStore'
import { useWorldBuilderStore } from '../../../stores/worldbuilderStore'
import { useSkillStore } from '../../../stores/skillStore'
import { useCommunityStore } from '../../../stores/communityStore'
import { useDungeonStore } from '../../../stores/dungeonStore'
import { useSocietyStore } from '../../../stores/societyStore'
import { useSceneStore } from '../../../stores/sceneStore'
import { useDocumentStore } from '../../../stores/documentStore'

export function BulkDataManager() {
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<{
    success: boolean
    message: string
    details?: Record<string, number>
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const dataManager = DataManager.getInstance()
  
  // 各ストアからデータを取得
  const { savedNames, importFromJSON: importNames } = useNameStore()
  const { savedPlots, importFromJSON: importPlots } = usePlotStore()
  const { savedTitles, importFromJSON: importTitles } = useTitleStore()
  const { characters, importFromJSON: importCharacters } = useCharacterStore()
  const { savedRelationships, importFromJSON: importRelationships } = useRelationshipStore()
  const { savedWikipedias, importFromJSON: importWikipedias } = useWikipediaStore()
  const { savedPrompts, importFromJSON: importPrompts, init: initPrompts } = useImagePromptStore()
  const { enemies, importFromJSON: importEnemies } = useEnemyStore()
  const { items, importFromJSON: importItems } = useItemStore()
  const { savedWorlds, importFromJSON: importWorlds } = useWorldBuilderStore()
  const { skills, importFromJSON: importSkills } = useSkillStore()
  const { savedCommunities, importFromJSON: importCommunities } = useCommunityStore()
  const { dungeons, importFromJSON: importDungeons } = useDungeonStore()
  const { savedSocieties, importFromJSON: importSocieties } = useSocietyStore()
  const { scenes, importFromJSON: importScenes } = useSceneStore()
  const { documents, importFromJSON: importDocuments } = useDocumentStore()
  
  // 画像プロンプトストアの初期化
  useEffect(() => {
    initPrompts()
  }, [initPrompts])
  
  // 統計情報の計算
  const statistics = dataManager.getStatistics({
    names: savedNames,
    plots: savedPlots,
    titles: savedTitles,
    characters: characters,
    relationships: savedRelationships,
    wikipedias: savedWikipedias,
    imagePrompts: savedPrompts,
    enemies: enemies,
    items: items,
    worlds: savedWorlds,
    skills: skills,
    communities: savedCommunities,
    dungeons: dungeons,
    societies: savedSocieties,
    scenes: scenes,
    documents: documents
  })
  
  // 一括エクスポート（JSON）
  const handleExportJSON = () => {
    setIsExporting(true)
    try {
      const jsonData = dataManager.exportAllAsJSON({
        names: savedNames,
        plots: savedPlots,
        titles: savedTitles,
        characters: characters,
        relationships: savedRelationships,
        wikipedias: savedWikipedias,
        imagePrompts: savedPrompts,
        enemies: enemies,
        items: items,
        worlds: savedWorlds,
        skills: skills,
        communities: savedCommunities,
        dungeons: dungeons,
        societies: savedSocieties,
        scenes: scenes,
        documents: documents
      })
      
      const blob = new Blob([jsonData], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `creanizer_all_data_${Date.now()}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }
  
  // 一括エクスポート（テキスト）
  const handleExportText = () => {
    setIsExporting(true)
    try {
      const textData = dataManager.exportAllAsText({
        names: savedNames,
        plots: savedPlots,
        titles: savedTitles,
        characters: characters,
        relationships: savedRelationships,
        wikipedias: savedWikipedias,
        imagePrompts: savedPrompts,
        enemies: enemies,
        items: items,
        worlds: savedWorlds,
        skills: skills,
        communities: savedCommunities,
        dungeons: dungeons,
        societies: savedSocieties,
        scenes: scenes,
        documents: documents
      })
      
      const blob = new Blob([textData], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `creanizer_all_data_${Date.now()}.txt`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }
  
  // インポート処理
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    console.log('[BulkDataManager] インポート処理開始')
    console.log('[BulkDataManager] ファイル名:', file.name)
    console.log('[BulkDataManager] ファイルサイズ:', file.size)
    
    setIsImporting(true)
    setImportResult(null)
    
    try {
      const text = await file.text()
      console.log('[BulkDataManager] ファイル読み込み完了, 文字数:', text.length)
      const importData = dataManager.validateAndParseImport(text)
      console.log('[BulkDataManager] データ検証完了:', importData)
      console.log('[BulkDataManager] インポートデータのキー:', Object.keys(importData.data || {}))
      
      const importDetails: Record<string, number> = {}
      let totalRequested = 0
      
      console.log('[BulkDataManager] 各データタイプのチェック開始')
      
      // 各データタイプをインポート
      if (importData.data.names && importData.data.names.length > 0) {
        const namesJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'names',
          data: importData.data.names
        })
        importNames(namesJson)
        importDetails.names = importData.data.names.length
        totalRequested += importData.data.names.length
      }
      
      if (importData.data.plots && importData.data.plots.length > 0) {
        const plotsJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'plots',
          data: importData.data.plots
        })
        importPlots(plotsJson)
        importDetails.plots = importData.data.plots.length
        totalRequested += importData.data.plots.length
      }
      
      if (importData.data.titles && importData.data.titles.length > 0) {
        const titlesJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'titles',
          data: importData.data.titles
        })
        importTitles(titlesJson)
        importDetails.titles = importData.data.titles.length
        totalRequested += importData.data.titles.length
      }
      
      if (importData.data.characters && importData.data.characters.length > 0) {
        const charactersJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'characters',
          data: importData.data.characters
        })
        importCharacters(charactersJson)
        importDetails.characters = importData.data.characters.length
        totalRequested += importData.data.characters.length
      }
      
      if (importData.data.relationships && importData.data.relationships.length > 0) {
        const relationshipsJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'relationships',
          data: importData.data.relationships
        })
        await importRelationships(relationshipsJson)
        importDetails.relationships = importData.data.relationships.length
        totalRequested += importData.data.relationships.length
      }
      
      if (importData.data.wikipedias && importData.data.wikipedias.length > 0) {
        const wikipediasJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'wikipedias',
          data: importData.data.wikipedias
        })
        importWikipedias(wikipediasJson)
        importDetails.wikipedias = importData.data.wikipedias.length
        totalRequested += importData.data.wikipedias.length
      }
      
      if (importData.data.imagePrompts && importData.data.imagePrompts.length > 0) {
        const promptsJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'imagePrompts',
          data: importData.data.imagePrompts
        })
        await importPrompts(promptsJson)
        importDetails.imagePrompts = importData.data.imagePrompts.length
        totalRequested += importData.data.imagePrompts.length
      }
      
      if (importData.data.enemies && importData.data.enemies.length > 0) {
        const enemiesJson = JSON.stringify({
          version: '1.0.0',
          type: 'enemies',
          exportDate: new Date().toISOString(),
          count: importData.data.enemies.length,
          data: importData.data.enemies
        })
        importEnemies(enemiesJson)
        importDetails.enemies = importData.data.enemies.length
        totalRequested += importData.data.enemies.length
      }
      
      if (importData.data.items && importData.data.items.length > 0) {
        console.log('[BulkDataManager] アイテムインポート開始:', importData.data.items.length, '件')
        console.log('[BulkDataManager] アイテムデータ:', importData.data.items)
        const itemsJson = JSON.stringify(importData.data.items)
        console.log('[BulkDataManager] アイテムJSON:', itemsJson)
        try {
          importItems(itemsJson)
          console.log('[BulkDataManager] importItems呼び出し完了')
        } catch (error) {
          console.error('[BulkDataManager] importItemsエラー:', error)
          // アイテムのインポートエラーは継続（他のデータタイプを処理）
        }
        importDetails.items = importData.data.items.length
        totalRequested += importData.data.items.length
      }
      
      if (importData.data.worlds && importData.data.worlds.length > 0) {
        const worldsJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'worlds',
          data: importData.data.worlds  // dataキーを使用（他と統一）
        })
        importWorlds(worldsJson)
        importDetails.worlds = importData.data.worlds.length
        totalRequested += importData.data.worlds.length
      }
      
      if (importData.data.skills && importData.data.skills.length > 0) {
        // スキルストア: JSON文字列形式（直接配列）
        const skillsJson = JSON.stringify(importData.data.skills)
        importSkills(skillsJson)
        importDetails.skills = importData.data.skills.length
        totalRequested += importData.data.skills.length
      }
      
      if (importData.data.communities && importData.data.communities.length > 0) {
        // コミュニティストア: JSON文字列形式（直接配列）
        const communitiesJson = JSON.stringify(importData.data.communities)
        importCommunities(communitiesJson)
        importDetails.communities = importData.data.communities.length
        totalRequested += importData.data.communities.length
      }
      
      if (importData.data.dungeons && importData.data.dungeons.length > 0) {
        // ダンジョンストア: 直接配列を渡す（any型）
        console.log('[BulkDataManager] ダンジョンインポート開始:', importData.data.dungeons.length, '件')
        console.log('[BulkDataManager] ダンジョンデータ:', importData.data.dungeons)
        try {
          importDungeons(importData.data.dungeons)
          console.log('[BulkDataManager] importDungeons呼び出し完了')
        } catch (error) {
          console.error('[BulkDataManager] importDungeonsエラー:', error)
          throw error
        }
        importDetails.dungeons = importData.data.dungeons.length
        totalRequested += importData.data.dungeons.length
      }
      
      if (importData.data.societies && importData.data.societies.length > 0) {
        // ソサエティストア: JSON文字列形式（dataキーを持つオブジェクト）
        console.log('[BulkDataManager] ソサエティインポート開始:', importData.data.societies.length, '件')
        console.log('[BulkDataManager] ソサエティデータ:', importData.data.societies)
        const societiesJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'societies',
          data: importData.data.societies
        })
        console.log('[BulkDataManager] ソサエティJSON:', societiesJson)
        try {
          importSocieties(societiesJson)
          console.log('[BulkDataManager] importSocieties呼び出し完了')
        } catch (error) {
          console.error('[BulkDataManager] importSocietiesエラー:', error)
          throw error
        }
        importDetails.societies = importData.data.societies.length
        totalRequested += importData.data.societies.length
      }
      
      if (importData.data.scenes && importData.data.scenes.length > 0) {
        // シーンストア: JSON文字列形式（dataキーを持つオブジェクト）
        console.log('[BulkDataManager] シーンインポート開始:', importData.data.scenes.length, '件')
        console.log('[BulkDataManager] シーンデータ:', importData.data.scenes)
        const scenesJson = JSON.stringify({
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'scenes',
          data: importData.data.scenes
        })
        console.log('[BulkDataManager] シーンJSON:', scenesJson)
        try {
          importScenes(scenesJson)
          console.log('[BulkDataManager] importScenes呼び出し完了')
        } catch (error) {
          console.error('[BulkDataManager] importScenesエラー:', error)
          throw error
        }
        importDetails.scenes = importData.data.scenes.length
        totalRequested += importData.data.scenes.length
      }
      
      if (importData.data.documents && importData.data.documents.length > 0) {
        // 文献ストア: Fileオブジェクトを期待
        console.log('[BulkDataManager] 文献インポート開始:', importData.data.documents.length, '件')
        console.log('[BulkDataManager] 文献データ:', importData.data.documents)
        const documentsJson = JSON.stringify({
          version: 1,
          exportDate: new Date().toISOString(),
          dataType: 'documents',
          data: importData.data.documents
        })
        const file = new File([documentsJson], 'import.json', { type: 'application/json' })
        try {
          await importDocuments(file)
          console.log('[BulkDataManager] importDocuments呼び出し完了')
        } catch (error) {
          console.error('[BulkDataManager] importDocumentsエラー:', error)
          // エラーは継続（他のデータタイプは既にインポート済み）
        }
        importDetails.documents = importData.data.documents.length
        totalRequested += importData.data.documents.length
      }
      
      console.log('[BulkDataManager] インポート処理完了')
      console.log('[BulkDataManager] インポート詳細:', importDetails)
      console.log('[BulkDataManager] 合計リクエスト数:', totalRequested)
      
      setImportResult({
        success: true,
        message: `${totalRequested}件のデータをインポート処理しました（重複は自動的にスキップされます）`,
        details: importDetails
      })
    } catch (error) {
      console.error('[BulkDataManager] インポート全体エラー:', error)
      console.error('[BulkDataManager] エラースタック:', (error as Error).stack)
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'インポートに失敗しました'
      })
    } finally {
      setIsImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  
  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        className="hidden"
      />
      
      {/* 統計情報 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Database className="h-5 w-5" />
          <h2 className="text-xl font-semibold">データ統計</h2>
        </div>
        <p className="text-muted-foreground mb-4">現在保存されているデータの概要</p>
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">名前</p>
              <p className="text-2xl font-bold">{statistics.names}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">プロット</p>
              <p className="text-2xl font-bold">{statistics.plots}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">タイトル</p>
              <p className="text-2xl font-bold">{statistics.titles}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">キャラクター</p>
              <p className="text-2xl font-bold">{statistics.characters}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">関係性</p>
              <p className="text-2xl font-bold">{statistics.relationships}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">架空事典</p>
              <p className="text-2xl font-bold">{statistics.wikipedias}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">画像プロンプト</p>
              <p className="text-2xl font-bold">{statistics.imagePrompts}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">エネミー</p>
              <p className="text-2xl font-bold">{statistics.enemies}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">アイテム</p>
              <p className="text-2xl font-bold">{statistics.items}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">世界観</p>
              <p className="text-2xl font-bold">{statistics.worlds}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">スキル</p>
              <p className="text-2xl font-bold">{statistics.skills || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">コミュニティ</p>
              <p className="text-2xl font-bold">{statistics.communities || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">ダンジョン</p>
              <p className="text-2xl font-bold">{statistics.dungeons || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">組織</p>
              <p className="text-2xl font-bold">{statistics.societies || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">シーン描写</p>
              <p className="text-2xl font-bold">{statistics.scenes || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">文献</p>
              <p className="text-2xl font-bold">{statistics.documents || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">合計</p>
              <p className="text-2xl font-bold text-primary">{statistics.total}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* エクスポート/インポート */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-2">一括データ管理</h2>
        <p className="text-muted-foreground mb-4">
          すべてのデータを一括でエクスポート・インポート
        </p>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleExportJSON}
              disabled={isExporting || statistics.total === 0}
              className="flex-1"
            >
              <FileJson className="h-4 w-4 mr-2" />
              JSON形式でエクスポート
            </Button>
            <Button
              onClick={handleExportText}
              disabled={isExporting || statistics.total === 0}
              variant="outline"
              className="flex-1"
            >
              <FileText className="h-4 w-4 mr-2" />
              テキスト形式でエクスポート
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              variant="outline"
              className="flex-1"
            >
              <Upload className="h-4 w-4 mr-2" />
              {isImporting ? 'インポート中...' : 'JSONファイルをインポート'}
            </Button>
          </div>
          
          {importResult && (
            <Alert className={importResult.success ? '' : 'border-destructive'}>
              {importResult.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {importResult.message}
                {importResult.details && (
                  <div className="mt-2 space-x-2">
                    {Object.entries(importResult.details).map(([key, value]) => (
                      <Badge key={key} variant="secondary">
                        {key}: {value}件
                      </Badge>
                    ))}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
          
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              JSONファイルには全データが含まれます。テキストファイルは人間が読みやすい形式で出力されます。
              インポート時は既存のデータに追加され、重複（タイトルと名前が同じもの）は自動的にスキップされます。
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  )
}